// src/components/Teacher/TeacherSidebar.js

import React, { useMemo } from 'react';
import {
  LayoutDashboard,
  CalendarDays,
  ClipboardList,
  BookOpen,
  FolderOpen,
  FileText,
  GraduationCap,
  Users,
  BarChart3,
  BriefcaseBusiness,
  Award,
  Settings,
  X
} from 'lucide-react';

import './TeacherSidebar.css';

const TeacherSidebar = ({
  activeMenu = 'dashboard',
  setActiveMenu,
  setShowSettings,
  t = {},
  teacher = {},
  userRole = 'teacher',
  teacherAccess = {},
  allowedMenus = [],
  isOpen = false,
  onClose
}) => {

  const normalizedRole = String(userRole || teacher.role || 'teacher')
    .trim()
    .toLowerCase();

  const role =
    normalizedRole === 'lab' ||
    normalizedRole === 'laborant' ||
    normalizedRole === 'лаборант'
      ? 'lab'
      : 'teacher';

  const panelSubtitle =
    role === 'lab'
      ? t.labPanel || 'Лаборант'
      : t.teacherPanel || 'Преподаватель';

  const logoSrc = `${process.env.PUBLIC_URL}/images/logo.png`;

  const accessValue = (sectionId) => {
    const value = teacherAccess?.[sectionId];

    if (value === undefined) {
      const defaultMenu = defaultMenuByRole[role] || defaultMenuByRole.teacher;
      if (defaultMenu.includes(sectionId)) return 'read';
      return 'none';
    }

    if (!value) return 'none';

    if (value === true) return 'edit';
    if (value === false) return 'none';

    return String(value).toLowerCase();
  };

  const hasAccess = (sectionId) => {
    if (sectionId === 'dashboard') return true;

    if (sectionId === 'reports') {
      const reports = accessValue('reports');
      const curator = accessValue('reportsCurator');
      const dept = accessValue('reportsDepartment');

      return (
        reports !== 'none' ||
        curator !== 'none' ||
        dept !== 'none'
      );
    }

    const level = accessValue(sectionId);

    return (
      level === 'read' ||
      level === 'view' ||
      level === 'edit' ||
      level === 'write'
    );
  };

  const menuItems = [
    {
      id: 'dashboard',
      name: t.sidebarDashboard || t.dashboard || 'Главная',
      icon: <LayoutDashboard size={20} strokeWidth={2.2} />
    },
    {
      id: 'schedule',
      name: t.sidebarSchedule || t.schedule || 'Расписание',
      icon: <CalendarDays size={20} strokeWidth={2.2} />
    },
    {
      id: 'plans',
      name: t.sidebarPlans || t.plans || 'Нагрузка',
      icon: <ClipboardList size={20} strokeWidth={2.2} />
    },
    {
      id: 'programs',
      name: t.sidebarPrograms || t.programs || 'Рабочие программы',
      icon: <BookOpen size={20} strokeWidth={2.2} />
    },

    {
      id: 'teacherFiles',
      name: t.sidebarTeacherFiles || t.teacherFiles || 'Личная папка',
      icon: <FileText size={20} strokeWidth={2.2} />
    },
    {
      id: 'teachers',
      name: t.sidebarTeachers || t.teachers || 'Преподаватели',
      icon: <Users size={20} strokeWidth={2.2} />
    },
    {
      id: 'reports',
      name: t.sidebarReports || t.reports || 'Отчеты',
      icon: <BarChart3 size={20} strokeWidth={2.2} />
    },

  ];

  const defaultMenuByRole = {
    teacher: [
      'dashboard',
      'schedule',
      'plans',
      'programs',

      'teacherFiles'
    ],
    lab: [
      'dashboard',
      'students',
      'schedule',
      'teachers'
    ]
  };

  const visibleMenuItems = useMemo(() => {
    return menuItems.filter((item) => {
      if (item.id === 'dashboard') return true;
      return hasAccess(item.id);
    });
  }, [teacherAccess, t]);

  const handleMenuClick = (menuId) => {
    if (typeof setActiveMenu === 'function') {
      setActiveMenu(menuId);
    }

    if (typeof setShowSettings === 'function') {
      setShowSettings(false);
    }
  };

  const handleSettingsClick = () => {
    if (typeof setShowSettings === 'function') {
      setShowSettings(true);
    }

    if (typeof setActiveMenu === 'function') {
      setActiveMenu('settings');
    }
  };

  return (
    <>
      {isOpen && (
        <div className="mobile-menu-overlay" onClick={onClose} />
      )}
      <aside className={`admin-sidebar teacher-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-top drawer-header-top">
          <div className="logo">
            <img
              src={logoSrc}
              alt={t.logoAlt || 'Кафедра информационных технологий'}
              className="logo-image"
            />

            <div className="logo-text">
              <h2>
                {t.sidebarLogoTitle ||
                  t.department ||
                  'Кафедра информационных технологий'}
              </h2>
              <p>{panelSubtitle}</p>
            </div>
          </div>
          <button type="button" className="drawer-close-btn" onClick={onClose} title="Закрыть">
            <X size={20} />
          </button>
        </div>

        <nav className="sidebar-nav">
          {visibleMenuItems.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`nav-item ${activeMenu === item.id ? 'active' : ''}`}
              onClick={() => handleMenuClick(item.id)}
              title={item.name}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-name">{item.name}</span>
            </button>
          ))}
        </nav>
      </aside>
    </>
  );

};

export default TeacherSidebar;